import { useCallback } from "react";
import { collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, Timestamp, updateDoc, where } from "firebase/firestore";
import { appointmentsCollections, consultationsCollection, firebaseBaseUrl, healthInstitutionCollection, reviewsCollection, wellnessClassesCollection, wellnessClassSubscriptionsCollection } from "../../../constants/firebase";
import { axiosClient } from "../../../config/axiosClient";
import { db } from "../../../config/firebase";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationPages, setActivePage, setCart, setSpecialties } from ".";
import axios from "axios";
import { Specialty } from "../../../types/User";
import { AppState } from "../..";
import { Service, WellnessClass, WellnessClassSubscription } from "../../../types/HealthInstitution";
import { toast } from "react-toastify";
import { getFunctions, httpsCallable } from "firebase/functions";

import { AppointmentPatientData, ClassSubscriptionRequest, ServiceOrderRequest, SubscribeToClassOnDateRequest } from "../../../types/Requests/ConsultationRequest";
import { ResponseStatus } from "../../../types/Requests/Status";
import { FirebaseError } from "firebase/app";
import { Review } from "../../../types/Consultation";
export function useSwitchPage(): (page: ApplicationPages)=> any{
  const dispatch = useDispatch()
  return useCallback((page) => {
      dispatch(setActivePage(page))
  }, [dispatch]);
}
export function useVerifyPayment(): (reference: any) => Promise<boolean> {
  return useCallback(async (reference) => {
    if (!reference) return false;
    try {
      const { data } = await axiosClient.post(
        `/payalt/verify-payment`,
        { reference }
      );

      return data.data.status === "success";
    } catch (error: any) {
      return false;
    }
  }, []);
}

export function useJoinAppointment(): (appointmentId: string, consultationId: string) => void {
  return useCallback(async (appointmentId, consultationId) => {
    try {
      await updateDoc(doc(db, consultationsCollection, consultationId, appointmentsCollections, appointmentId), {
        patientJoined: true,
        patientJoinedTime: new Date(),
      })
      return
    } catch (error) {
      console.log(' updating provider joined status', error)
    }
  }, [])
}
export function useUpdateAppointmentUserState(): (appointmentId: string, consultationId: string, label: any, status: any) => void {
  return useCallback(async (appointmentId, consultationId, label, status) => {
    try {
      await updateDoc(doc(db, consultationsCollection, consultationId, appointmentsCollections, appointmentId), {
        [label]: status,
      })
      return
    } catch (error) {
    }
  }, [])
}

export function useFetchDoctorById(): (doctorId: string) => Promise<any> {
  return useCallback(async (doctorId) => {
    try {
      const response = await axios.post(`${firebaseBaseUrl}/systemalt/doctor-metrics`, { doctorId })
      return response.data
    } catch (error) {
    }
  }, [])
}

export function useDoctorSpecialties(): () => void {
  const dispatch = useDispatch();
  return useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, `practitioners`));
      const specialties: Specialty[] = [];
      querySnapshot.forEach((doc) => {
        // doc.data() is never undefined for query doc snapshots
        specialties.push({ id: doc.id, ...doc.data() } as Specialty);
      });
      dispatch(setSpecialties(specialties));
    } catch (error) {
    }
  }, [dispatch]);
}

export function useUpdateCart(): (services: Service[]) => void{
  const dispatch = useDispatch()
  const { cart } = useSelector<AppState, AppState["application"]>(
    (state) => state.application
  );
  return useCallback(
    (services) => {
      try {
        services.forEach((service)=>{
          const isChecked = cart.some((item) => service.id === item.id);
          if (isChecked) {
            cart?.filter((cartItem) => cartItem.id !== service.id)
              .length > 0
              ? dispatch(
                  setCart([...cart?.filter(
                    (cartItem) => cartItem.id !== service.id
                  )])
                )
              : dispatch(setCart([]));
          } else {
            dispatch(
              setCart([
                ...cart,
                    {
                      id: service.id,
                      name: service.title,
                      quantity: 1,
                      price: service.cost,
                      type: service.category,
                      image: service.image
                    },
              ],)
            );
          }
        })
       
      } catch (error) {
        
      }
    },
    [cart],
  )
  
}

export function useCreateServiceOrderItem(): (serviceOrder: ServiceOrderRequest, callback: any) => Promise<void> {    

  return useCallback(async (serviceOrder: any, callback: (arg0: any) => void) => {
   try {
     const { data } = await axiosClient.post(
       `/institutionalt/create-service-order`,
       serviceOrder
     );
     callback(data)
   } catch (error: any) {
     const errorData = error.response.data

     toast.error(`${errorData.message || 'Error Contact Support'}`)
     callback({
       status: 'Error',
       code: errorData.code??0,
       message: errorData.message || 'Error',
       data:errorData
     })
   }
 }, []);
}

export function useBookWellnessClass(): (
  bookingDetails: Partial<SubscribeToClassOnDateRequest>, callback: (val: any)=>void
) => Promise<null> {
  const functions = getFunctions();
  const subscribeToClassOnDate = httpsCallable(functions, "subscribeToClassOnDate");

  return useCallback(async (bookingDetails, callback) => {
    try {
      await subscribeToClassOnDate({
        ...bookingDetails
      }).then(async ({data}: any) => {
        console.log('valuee', data)
        if(data?.status === 'Error'){
          callback({
            status: 'Error',
            code: data.code??0,
            message: data.message || 'Error',
            data:data
          })
        }else{
          callback({
            status: ResponseStatus.SUCCESS,
            data,
          })
        }
        
      });
      return null
    } catch (error: any) {
      console.log('error in useBookWellnessClass', error)
      toast.error(error.message)
      callback({
        status: 'Error',
      })
      return null;
    }

  }, []);
}
export function useWellnessClasses():  (facilityId: string) => Promise<WellnessClass[]> {
  return useCallback(async (facilityId) => {
    try {
      console.log('useWellnessClasses')
      const querySnapshot = await getDocs(
        collection(db, healthInstitutionCollection, facilityId, wellnessClassesCollection)
      )
     return querySnapshot.docs.map(doc=>{
        return { ...doc.data(), id: doc.id } as WellnessClass
      })
    } catch (error) {
      console.error('error in getting useWellnessClasses', error)
      return []
    }
  }, [])
}

export function useWellnessClassById():  (bookingId: string) => Promise<WellnessClassSubscription | null> {
  return useCallback(async (bookingId) => {
    try {
      console.log('useWellnessClasses')
      const queryData = await getDoc(
        doc(db, wellnessClassSubscriptionsCollection, bookingId)
      )
      return queryData.data() as WellnessClassSubscription;
    } catch (error) {
      console.error('error in getting useWellnessClasses', error)
      return null
    }
  }, [])
}

export function useReviewClass(): (
  data: {
    skipReview: boolean;
    reviewData: {
      rating: number;
      feedback: string;
      bookedClassId: string;
      patientData: AppointmentPatientData;
      facilityId: string;
    };
  }
) => Promise<void> {
  return useCallback(async (data) => {
    try {
      const {
        skipReview,
        reviewData: {
          rating,
          feedback,
          patientData,
          bookedClassId,
          facilityId,
        },
      } = data;

      if (skipReview) {
        await updateDoc(
          doc(db, wellnessClassSubscriptionsCollection, bookedClassId),
          { review: "SKIPPED" }
        )
      } else {
        const review: Review = {
          rating,
          feedback,
          patientData,
          createdAt: new Date() as unknown as Timestamp,
          appointmentId: bookedClassId,
          type: "WELLNESS_CLASS",
          facilityId,
        }

        console.log("useReviewClass", review)

        const reviewRef = doc(collection(db, reviewsCollection))

        await setDoc(reviewRef, review)

        console.log("reviewRef", reviewRef.id)

        await updateDoc(
          doc(db, wellnessClassSubscriptionsCollection, bookedClassId),
          {
            review: {
              id: reviewRef.id,
              rating,
            },
          }
        )
        toast.success('Review Successfully Sent')
      }
    } catch (error) {
      console.error("error reviewing appointment", error)
    }
  }, [])
}


